import { useState, useEffect, useRef } from "react";
import { FormDataType, getOperatorFeeRate } from "../types/profitTracking";

/** Convertit une dépense en (CAD, XAF) selon sa devise et le taux IZI. */
export const expenseToCadXaf = (
  amount: number,
  currency: string | undefined,
  iziRate: number
): { cad: number; xaf: number } => {
  const cur = (currency || "XAF").toUpperCase();
  const amt = amount || 0;
  const rate = iziRate || 0;
  if (cur === "CAD" || cur === "USD") {
    return { cad: amt, xaf: rate > 0 ? amt * rate : 0 };
  }
  return { xaf: amt, cad: rate > 0 ? amt / rate : 0 };
};

/** Bén. Final (CAD) = Revenu (CAD) − Dépenses (converties en CAD). */
export const computeFinalProfits = (
  netProfit: number,
  profitCad: number,
  expenseAmount: number,
  expenseCurrency: string | undefined,
  iziRate: number,
  direction: string
): { final_profit: number; final_profit_cad: number } => {
  const { cad: expenseCad, xaf: expenseXaf } = expenseToCadXaf(expenseAmount, expenseCurrency, iziRate);
  if (direction === "CAD_TO_XAF") {
    return {
      final_profit: parseFloat((netProfit - expenseXaf).toFixed(2)),
      final_profit_cad: parseFloat((profitCad - expenseCad).toFixed(4)),
    };
  }
  return {
    final_profit: parseFloat((netProfit - expenseCad).toFixed(2)),
    final_profit_cad: parseFloat((profitCad - expenseCad).toFixed(4)),
  };
};

export const useProfitCalculations = (initialFormData: FormDataType) => {
  const [formData, setFormData] = useState<FormDataType>(initialFormData);
  const prevDirectionRef = useRef(initialFormData.direction);

  // Auto-calculate fields
  useEffect(() => {
    const { capital_amount, buy_rate, sell_rate, izi_rate, direction, payment_method } = formData;

    if (capital_amount > 0 && buy_rate > 0) {
      const buyFees = 0.001 * (capital_amount / buy_rate);
      const usdt = capital_amount / buy_rate - buyFees;

      let saleAmt = 0;
      let iziAmt = 0;
      let grossProfit = 0;
      let feesAmt = 0;
      let netProfit = 0;
      let profitCad = 0;
      let profitPct = 0;

      if (sell_rate > 0) {
        saleAmt = usdt * sell_rate;
      }

      if (izi_rate > 0) {
        if (direction === "CAD_TO_XAF") {
          iziAmt = capital_amount * izi_rate;
          grossProfit = saleAmt - iziAmt;
          feesAmt = getOperatorFeeRate(payment_method) * iziAmt;
          netProfit = grossProfit - feesAmt;
          profitCad = netProfit / izi_rate;
          profitPct = iziAmt > 0 ? (grossProfit / iziAmt) * 100 : 0;
        } else {
          iziAmt = capital_amount / izi_rate;
          grossProfit = saleAmt - iziAmt;
          feesAmt = 0;
          netProfit = grossProfit;
          profitCad = netProfit;
          profitPct = iziAmt > 0 ? (grossProfit / iziAmt) * 100 : 0;
        }
      }

      setFormData((prev) => ({
        ...prev,
        buy_fees: parseFloat(buyFees.toFixed(6)),
        usdt_obtained: parseFloat(usdt.toFixed(6)),
        sale_amount: parseFloat(saleAmt.toFixed(2)),
        izi_amount: parseFloat(iziAmt.toFixed(2)),
        gross_profit: parseFloat(grossProfit.toFixed(2)),
        fees: parseFloat(feesAmt.toFixed(2)),
        net_profit: parseFloat(netProfit.toFixed(2)),
        profit_cad: parseFloat(profitCad.toFixed(4)),
        profit_percentage: parseFloat(profitPct.toFixed(4)),
      }));
    }
  }, [
    formData.capital_amount,
    formData.buy_rate,
    formData.sell_rate,
    formData.izi_rate,
    formData.direction,
    formData.payment_method,
  ]);

  // Recalculate final profit — Bén. CAD = Revenu CAD − Dépenses (en CAD)
  useEffect(() => {
    setFormData((prev) => {
      const { final_profit, final_profit_cad } = computeFinalProfits(
        prev.net_profit || 0,
        prev.profit_cad || 0,
        prev.expense_amount || 0,
        prev.expense_currency,
        prev.izi_rate || 0,
        prev.direction
      );
      if (prev.final_profit === final_profit && prev.final_profit_cad === final_profit_cad) {
        return prev;
      }
      return { ...prev, final_profit, final_profit_cad };
    });
  }, [
    formData.net_profit,
    formData.profit_cad,
    formData.expense_amount,
    formData.expense_currency,
    formData.izi_rate,
    formData.direction,
  ]);

  // Devise capital / défaut dépenses uniquement quand la direction change vraiment
  useEffect(() => {
    if (prevDirectionRef.current === formData.direction) return;
    prevDirectionRef.current = formData.direction;
    setFormData((prev) => ({
      ...prev,
      capital_currency: prev.direction === "CAD_TO_XAF" ? "CAD" : "XAF",
      expense_currency: prev.direction === "CAD_TO_XAF" ? "XAF" : "CAD",
    }));
  }, [formData.direction]);

  const updateFormData = (updates: Partial<FormDataType>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const resetFormData = () => {
    prevDirectionRef.current = initialFormData.direction;
    setFormData(initialFormData);
  };

  return { formData, updateFormData, resetFormData };
};
